import { useState, useContext, useEffect, useRef } from 'react';
import { GridColDef, GridEventListener, GridRenderCellParams, GridRowSelectionModel, useGridApiRef   } from '@mui/x-data-grid';
import { Button, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { ArrowCircleDown, Edit, Visibility } from '@mui/icons-material';
import { TMeltingLot, TLineDetails } from '../../Models/Inbound';
import Table from '../custom/Table';
import Title from '../custom/Title';
import MeltingLot from './UpdateInboundMeltingLot';
import { TransactionContext } from '../../contexts/TransactionContext';
import InboundLine from './UpdateInboundLine';
import { NotificationContext } from '../../contexts/NotificationContext';
import { CustomerContext } from '../../contexts/CustomerContext';
import useService from '../../hooks/useService';
import { downloadSlip, getLineDetails, getLotDetails, fetchConsentForm, createOrUpdateLineDetails, convertToPurityTesting, fetchXRFReport } from '../../services/Transactions';
import { TColumn } from '../../Models/common';
import { LINE_PRODUCT_TYPE, LOT_WEIGHT_LIMIT, TRANSACTION_STATUS, TRANSACTION_TYPES } from '../../utils/constant';
import { Tooltip } from '@mui/material'
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import { getThreePointNumber } from '../../utils/helpers';

export type TLineDetailsColumn = TLineDetails & {
    actions: string;
}
export const INBOUND_LINE_COLUMNS: GridColDef<TLineDetailsColumn>[] = [
    {
        field: "actions",
        headerName: 'Action',
    },
    {
        field: "metalType",
        headerName: 'Metal Type',
        renderCell(params) {
            return <Typography style={{ textTransform: 'capitalize' }}>{params.row.metalType}</Typography>
        },
    },
    {
        field: "lotId",
        headerName: 'Lot No',
    },
    {
        field: "declaredWeight",
        headerName: 'Declared Weight(GR)',
        minWidth: 150,
    },
    {
        field: "netWeight",
        headerName: 'Net Weight(GR)',
        minWidth: 120
    },
]

type TMeltingLotColumns = TMeltingLot & {
    actions: any;
}

export const INBOUND_MELTING_LOT_COLUMN: TColumn<TMeltingLotColumns>[] = [
    {
        field: "actions",
        headerName: 'Action',
    },
    {
        field: "id",
        headerName: 'Lot No',
    },
    {
        field: "declaredWeight",
        headerName: 'Declared Weight(GR)',
        minWidth: 150
    },
    {
        field: "netWeight",
        headerName: 'WBM(GR)',
        minWidth: 120
    },
    {
        field: "weightAfterMelting",
        headerName: 'WAM',
    },
    {
        field: "customerTakenWeight",
        headerName: 'CTW (GR)',
        minWidth: 200
    },
    {
        field: "settlementNetWeight",
        headerName: 'Settlement Net Weight (GR)',
        minWidth: 200
    },
    {
        field: "fineness",
        headerName: 'Fineness',
    },
    {
        field: "fineWeight",
        headerName: 'Fine Weight (GR)',
        minWidth: 130
    },
    {
        field: "wamt",
        headerName: 'WAMT (GR)',
    },
    {
        field: "fineWeightToReturn",
        headerName: 'Settlement Fine Weight (GR)',
        minWidth: 200
    },
    {
        field: "finalSettlementNetWeight",
        headerName: 'Final Settlement Net Weight (GR)',
        minWidth: 260
    },
    {
        field: "furnace",
        headerName: 'Furnace',
    },
]

function LineDetails() {
    const { meltingLot, lineDetails, transactionInfo: { globalTransactionNumber, status: transactionStatus, transactionType }, inboundDetails } = useContext(TransactionContext)
    const { customerConfig } = useContext(CustomerContext)
    const { notify } = useContext(NotificationContext)
    const [showInboundLineForm, setShowInboundLineForm] = useState<boolean | string>(false)
    const [showMeltingLotForm, setShowMeltingLotForm] = useState<boolean | string>(false)
    const [selectedLines, setSelectedLines] = useState<string[]>([])
    const [selectedLots, setSelectedLots] = useState<string[]>([])
    const { execute } = useService()
    const isLineAlreadyIncluded = (lineId: string) => Boolean(lineDetails.filter(line => line.id === lineId)[0]?.lotId)
    let isLotCompleted : any
        if(transactionType=== TRANSACTION_TYPES.PURITY_TESTING){
        isLotCompleted = (lotId: string) => typeof lotId === 'string' && Boolean(meltingLot.filter(lot => lot.id === lotId)[0].wamt && meltingLot.filter(lot => lot.id === lotId)[0].fineness )
    }else{
        isLotCompleted = (lotId: string) => typeof lotId === 'string' && Boolean(meltingLot.filter(lot => lot.id === lotId)[0].fineWeightToReturn)
    }
    // const [openMenu,setOpenMenu] = useState<boolean>(false);
    const [slipMenuAnchorEl, setSlipMenuAnchorEl] = useState(null);
    const open = Boolean(slipMenuAnchorEl);
    const [slipType, setSlipType] = useState<string>("");
    const [lotId, setLotId] = useState<string>("");
    const handleClick = (lotId: string, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setSlipMenuAnchorEl(e.currentTarget as any)
        setLotId(lotId);
    };
    const handleClose = (type: string) => {
        setSlipType(type);
        setSlipMenuAnchorEl(null)
    }



    const getSlip = async (lotId: string) => {
        if (globalTransactionNumber) {
            const { data } = await execute(() => downloadSlip({ lotId, transactionType, slipType, globalTransactionNumber }))
            console.log(data, "slipData");
            if (data !== null) {
                // Assuming data is the base64 string of the PDF received from the backend
                const base64Data = data.data;

                // Convert base64 to blob
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });

                // Create object URL
                const url = URL.createObjectURL(blob);

                // Open the PDF in a new tab
                window.open(url, '_blank');
            }
        }
    }
    useEffect(() => {
        if (slipType.length && !open) {
            getSlip(lotId);
        }
    }, [slipType, open])

    const handleDeleteLineItem = async (id: any) => {
        const { error } = await execute(() => createOrUpdateLineDetails({
            globalTransactionNumber: globalTransactionNumber!,
            deleteLineItem: true,
            transactionType ,
            lineDetails: {  
                id: id,
                barNumber: '',
                netWeight: 0,
                productType: '',
                declaredWeight: 0,
                declaredFineness: 0,
                lineDescription: '',
                manualEntryMade: false
            }
        }))
        if (error === null) {
            notify('Inbound line deleted')
            if (globalTransactionNumber) execute(() => getLineDetails({ globalTransactionNumber }))
        }
    }
    const handleDeleteLotItem = async (id: any) => {
        const { error } = await execute(() => convertToPurityTesting({
            globalTransactionNumber: globalTransactionNumber!,
            transactionType ,
            Lotids: id
        }))
        if (error === null) {
            notify('Lot(s) Converted To Purity Testing Transaction Successfully')
            if (globalTransactionNumber){
                execute(() => getLineDetails({ globalTransactionNumber }))
                execute(() => getLotDetails({ globalTransactionNumber }))
            } 
        }
    }
    const getLineColumns = () => {
        return INBOUND_LINE_COLUMNS.map(field => {
            const row: GridColDef<TLineDetailsColumn> = {
                ...field
            }
            switch (field.field) {
                case "lotId":
                    row.sortable = true
                    row.sortComparator = (v1, v2) => {
                        const getNumericValue = (value: string) => value ? value.match(/\d+/g):[]
                        
                        const num1 = getNumericValue(v1)
                        const num2 = getNumericValue(v2)

                        return Number(num1?.join('') || 0) - Number(num2?.join('') || 0)
                    }
                    row.renderCell = params => params.row.lotId
                break;
                case "actions":
                    row.renderCell = (params) => {
                        return <>
                            <IconButton onClick={(event) => {
                                event.stopPropagation()
                                setShowInboundLineForm(params.id as string)
                            }}>
                                {isLineAlreadyIncluded(params.id as string) ? <Visibility /> : <Edit />}
                            </IconButton>
                            <IconButton>
                                {isLineAlreadyIncluded(params.id as string) ? <></> : <DeleteIcon onClick={(event) => {
                                    event.stopPropagation()
                                    handleDeleteLineItem(params.id as string)
                                }} />}
                            </IconButton>
                        </>
                    }
                    break;
            }

            return row
        })
    }

    const getMeltingColumns = () => {
        return INBOUND_MELTING_LOT_COLUMN.map(column => {
            const row: GridColDef<TMeltingLotColumns> = {
                ...column
            }
            switch (column.field) {
                case "id":
                    row.sortable = true
                    row.sortComparator = (v1, v2) => {
                        const getNumericValue = (value: string) => value ? value.match(/\d+/g):[]
                        
                        const num1 = getNumericValue(v1)
                        const num2 = getNumericValue(v2)

                        return Number(num1?.join('') || 0) - Number(num2?.join('') || 0)
                    }
                    row.renderCell = params => params.row.id
                break;
                case "actions":
                    row.width = 150
                    row.renderCell = (params) => {
                        return <div style={{ display: "flex", position: "relative" }}>
                            <IconButton onClick={() => setShowMeltingLotForm(params.id as string)} >
                                {!isLotCompleted(params.id as string) ? <Edit /> : <Visibility />}
                            </IconButton>
                            {
                                isLotCompleted(params.id as string) &&
                                <div style={{ position: 'relative' }}>
                                    <IconButton id="slip-button" aria-controls={open ? 'basic-menu' : undefined} aria-haspopup="true" aria-expanded={open ? 'true' : undefined} onClick={(e) => handleClick(params.row.id as string, e)}>
                                        <PrintIcon />
                                    </IconButton>
                                    <Menu anchorOrigin={{ horizontal: 'center', vertical: 'center' }} anchorEl={slipMenuAnchorEl} id="basic-menu" open={open} onClose={handleClose} MenuListProps={{ 'aria-labelledby': 'slip-button' }}>
                                        <MenuItem sx={{ '&:hover': { backgroundColor: '#cfa04f', color: 'primary.contrastText' } }} onClick={() => handleClose('WBM')}>Token WBM</MenuItem>
                                        <MenuItem sx={{ '&:hover': { backgroundColor: '#cfa04f', color: 'primary.contrastText' } }} onClick={() => handleClose('WAM')}>Token WAM</MenuItem>
                                        <MenuItem sx={{ '&:hover': { backgroundColor: '#cfa04f', color: 'primary.contrastText' } }} onClick={() => handleClose('WAMT')}>Token WAMT</MenuItem>
                                        <MenuItem sx={{ '&:hover': { backgroundColor: '#cfa04f', color: 'primary.contrastText' } }} onClick={() => handleClose('LOT')}>Lot Details</MenuItem>
                                        {/* <MenuItem sx={{'&:hover': { backgroundColor: '#cfa04f', color: 'primary.contrastText' }}} onClick={()=>handleClose('XRF')}>XRF Certificate</MenuItem> */}
                                    </Menu>
                                    <Tooltip title="Download XRF Report" style={{}}>
                                        <Button onClick={() => downloadXRFReport(params)} color='secondary' style={{ color: 'grey',  marginLeft: '-10px' }}><ArrowCircleDown /></Button>
                                    </Tooltip>
                                </div>
                            }
                        </div>
                    }
                    break;
            }
            return row
        })
    }

    const addMeltingLotHandler = () => {
        if (!selectedLines.length) notify('Select an inbound line first.', 'error')
        if (lineDetails.filter(line => selectedLines.includes(line.id!)).reduce((totalWeight, item) => totalWeight + item.netWeight, 0) >= LOT_WEIGHT_LIMIT) notify(`Maximum lot size limit (${LOT_WEIGHT_LIMIT}gm) breached`, 'error')
        else setShowMeltingLotForm(true)
    }

    const handleConvertToPurityTesting = () =>{
        handleDeleteLotItem(selectedLots)     
    }

    function arrayBufferToBase64(buffer: any) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    async function createConsentForm() {

        let lotNo = lineDetails[0]?.lotId || '';
        let productType = lineDetails[0]?.productType || '';
        // @ts-ignore
        let netweight = lineDetails[0]?.netWeight || '';
        let customerName = customerConfig?.fullName || '';
        let customerRefNo = customerConfig?.customerRefNo || '';
        let billingAddressId = inboundDetails?.billingAddressId || '';
        let serialNo = inboundDetails.id || '';
        let lotDetails = lineDetails || '';

        if (!lotNo) {
            return notify('lot number not avialable', 'error')
        } else {

            if (!billingAddressId) {
                return notify('Address not available', 'error')
            } else {
                console.log('lot number available')
                console.log(lotNo, productType, netweight, customerName, customerRefNo, billingAddressId)
                if (globalTransactionNumber) {
                    //@ts-ignore
                    const { data, error } = await execute(() => fetchConsentForm({ lotDetails, lotNo, productType, netweight, customerName, customerRefNo, billingAddressId, transactionType, serialNo,globalTransactionNumber }))
                    console.log(data, error, "slipData");
                    //@ts-ignore
                    const base64String = arrayBufferToBase64(data.data);
                    var link = document.createElement('a');
                    link.href = `data:application/pdf;base64,${base64String}`
                    var fileName = "ConsentForm";
                    link.download = fileName;
                    link.click();
                }
            }
        }
    }


    console.log(transactionStatus, 'inbound_details')

    const lineSelectHandler = (selectionModel: GridRowSelectionModel) => {
        console.log("line details slecteing", lineDetails)
        if (selectionModel.length && Boolean(lineDetails.filter(line => line.id === selectionModel[selectionModel.length - 1])[0].lotId)) return notify('Line Detail already included in a Lot', 'error')
        console.log('selected line', selectionModel)
        // check if melted- non melted linedetails merged condition
        if (transactionType === TRANSACTION_TYPES.BUY_BACK) {
            if (selectionModel.length > 1) {

                let meltedProduct = lineDetails.filter((item: any) =>
                    selectionModel.includes(item.id) && item.productType === LINE_PRODUCT_TYPE['MMTC-PAMP Product']
                );
                console.log("meltedProduct is ====>", meltedProduct)
                if (meltedProduct.length !== 0 && meltedProduct.length !== selectionModel.length)
                    return notify('Lot with assured and non-assured product is not possible', 'error')
            }
            setSelectedLines(selectionModel as string[])
        } else {
            setSelectedLines(selectionModel as string[])
        }
    }
    const lotSelectHandler = (selectionModel: GridRowSelectionModel) => {
        if (transactionType === TRANSACTION_TYPES.BUY_BACK) {
            if (selectionModel.length > 1) {

                let meltedProduct = lineDetails.filter((item: any) =>
                    selectionModel.includes(item.id) && item.productType === LINE_PRODUCT_TYPE['MMTC-PAMP Product']
                );
                console.log("meltedProduct is ====>", meltedProduct)
                if (meltedProduct.length !== 0 && meltedProduct.length !== selectionModel.length)
                    return notify('Lot with assured and non-assured product is not possible', 'error')
            }
            setSelectedLots(selectionModel as string[])
        } else {
            setSelectedLots(selectionModel as string[])
        }
    }

    const closeLineForm = () => {
        setShowInboundLineForm(false)
        setSelectedLines([])
        if (globalTransactionNumber) {
            execute(() => getLotDetails({ globalTransactionNumber }))
            execute(() => getLineDetails({ globalTransactionNumber }))
        }
    }

    const closeLotForm = () => {
        setShowMeltingLotForm(false)
        setSelectedLines([])
        if (globalTransactionNumber) {
            execute(() => getLotDetails({ globalTransactionNumber }))
            execute(() => getLineDetails({ globalTransactionNumber }))
        }
    }

    useEffect(() => {
        if (globalTransactionNumber) {
            execute(() => getLineDetails({ globalTransactionNumber }))
            execute(() => getLotDetails({ globalTransactionNumber }))
        }
    }, [])

    const apiRef: any = useGridApiRef();

    const handleScrollEventFromDataGrid: GridEventListener<'scrollPositionChange'> = (
        params,  // GridScrollParams
        event,   // MuiEvent<React.UIEvent | MuiBaseEvent>
        details, // GridCallbackDetails
      ) => {
        const rowToScroll = document.getElementById('totalRow');
        //@ts-ignore
        rowToScroll.scrollLeft = (params.left);
      }

    const eventsToSubscribe: any = [
        {
            event:"scrollPositionChange",
            handler:handleScrollEventFromDataGrid
        }
    ];

   
     useEffect(() => {
        getTotalMetligValues();
     }, [meltingLot])

     type TLotTotals = {
        totalDeclaredWeight: number
        totalWBM: number
        totalWAM:number
        totalCTW:number
        totalSettlementNetWeight:number
        totalFineWeight:number
        totalWAMT:number
        totalSettlementFineWeight:number
        totalFinalSettlementNetWeight:number
     }

     let totalValues :TLotTotals  = {
        totalDeclaredWeight : 0,
        totalWBM : 0,
        totalWAM : 0,
        totalCTW : 0,
        totalSettlementNetWeight : 0,
        totalFineWeight : 0,
        totalWAMT : 0,
        totalSettlementFineWeight : 0,
        totalFinalSettlementNetWeight : 0
    }


     function getTotalMetligValues(){
        if(meltingLot && meltingLot.length > 0){
            for( let i = 0 ; i < meltingLot.length ; i++ ){
                totalValues.totalDeclaredWeight += meltingLot[i]?.declaredWeight;
                totalValues.totalWBM += meltingLot[i]?.netWeight;
                totalValues.totalWAM += meltingLot[i]?.weightAfterMelting;
                totalValues.totalCTW += meltingLot[i]?.customerTakenWeight;
                totalValues.totalFineWeight += meltingLot[i]?.fineWeight;
                totalValues.totalWAMT += meltingLot[i]?.wamt;
                totalValues.totalSettlementNetWeight += meltingLot[i]?.settlementNetWeight;
                totalValues.totalSettlementFineWeight += meltingLot[i]?.fineWeightToReturn;
                totalValues.totalFinalSettlementNetWeight += meltingLot[i]?.finalSettlementNetWeight
            }
            
            // for (let totalValuesKey in totalValues) {
            //      // Check if the number has more than 3 decimal places
            //     //  @ts-ignore
            //     const number = totalValues[key];
            //     const decimalPlaces = (number.toString().split('.')[1] || '').length;
            //     if (decimalPlaces > 3) {
            //             // Round the number to 3 decimal places
            //             //  @ts-ignore
            //             totalValues[key] = Number(number.toFixed(3));
            //         }

            //      totalValues[key] = totalValues[key] === 0 ? "" : totalValues[key];
                
            //   }

            const totalValuesKeys = Object.keys(totalValues) as (keyof TLotTotals)[]
            totalValuesKeys.forEach(key => totalValues[key] = getThreePointNumber(totalValues[key]))

          }else{
            const totalRowElement = document.getElementById('totalRow');
            if (totalRowElement) {
              totalRowElement.style.display = "none";
            }
          }
     }
     getTotalMetligValues();
     const totalRowRef : any = useRef();
     


      //@ts-ignore
      const handleTotalRowScroll = scroll => {
        apiRef.current.scroll({
            left: scroll.target.scrollLeft, // Horizontal scroll position
            top: 0, // Vertical scroll position (if needed)
            behavior: 'auto', // Scroll behavior (optional)
          })
      };
 
      const downloadXRFReport = async(params: GridRenderCellParams) => {
        const { data, error } = await execute(() => fetchXRFReport({ transactionType, gtNumber: globalTransactionNumber as string, lotId: params.row.id }))
        console.log(data, error);
        //@ts-ignore
        const base64String = arrayBufferToBase64(data.data);
        var link = document.createElement('a');
        link.href = `data:application/pdf;base64,${base64String}`
        var fileName = `XRFReport_${params.row.id}`;
        link.download = fileName;
        link.click();
      }

   

    const LineLotTables = () => {
        return (
            <div style={{ padding: '0 10px' }}>
                <div >
                    <Title style={{ textAlign: 'left', margin: '20px 10px 10px 0', color: '#7f7d76' }}>Inbound Line</Title>
                    <Table
                        // rowSelection
                        rowSelectionModel={selectedLines}
                        checkboxSelection
                        onRowSelectionModelChange={lineSelectHandler}
                        columns={getLineColumns()}
                        rows={lineDetails}
                        getRowClassName={(params) => selectedLines.includes(params.id as string) ? 'selected_row' : ''}
                        slotProps={{ row: { style: { cursor: 'pointer' } } }}
                        initialState={{
                            sorting: {
                              sortModel: [{ field: 'lotId', sort: 'asc' }],
                            },
                        }}
                    />
                </div>


                
                    <div style={{ overflow: 'auto',position:'relative' }} >
                        <div style={{ position: 'relative', display: 'flex' }}>
                            <Title style={{ textAlign: 'left', margin: '20px 10px 10px 0', color: '#7f7d76' }}>Inbound Melting Lot</Title>

                            <Tooltip title="Download Consent Form" style={{}}>
                                <Button onClick={() => createConsentForm()} color='secondary' style={{ color: 'grey', marginTop: '15px' }}><ArrowCircleDown /></Button>
                            </Tooltip>
                        </div>
                     
                        <div className='scroll_bar_hide'>
                        <Table
                            rowSelectionModel={selectedLots}
                            checkboxSelection
                            onRowSelectionModelChange={lotSelectHandler}
                            columns={getMeltingColumns()}
                            rows={meltingLot}
                            apiRef={apiRef}
                            subscribeEvents={eventsToSubscribe}
                            initialState={{
                                sorting: {
                                  sortModel: [{ field: 'id', sort: 'asc' }],
                                },
                            }}
                        />
                        </div>
                  
                        <div id={"totalRow"} style={{width:'100%',overflowX:"scroll"}} className='scroll_bar_show'onScroll={handleTotalRowScroll} ref={totalRowRef}>
                            <tr>
                                <td style={{width:'100px',minWidth:'100px',maxWidth:'100px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}>Total</td>
                                <td style={{width:'100px',minWidth:'100px',maxWidth:'100px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}></td>
                                <td style={{width:'150px',minWidth:'150px',maxWidth:'150px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}>{totalValues.totalDeclaredWeight}</td>
                                <td style={{width:'120px',minWidth:'120px',maxWidth:'120px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}>{totalValues.totalWBM}</td>
                                <td style={{width:'100px',minWidth:'100px',maxWidth:'100px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}>{totalValues.totalWAM}</td>
                                <td style={{width:'200px',minWidth:'200px',maxWidth:'200px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}>{totalValues.totalCTW}</td>
                                <td style={{width:'200px',minWidth:'200px',maxWidth:'200px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}>{totalValues.totalSettlementNetWeight}</td>
                                <td style={{width:'100px',minWidth:'100px',maxWidth:'100px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}></td>
                                <td style={{width:'130px',minWidth:'130px',maxWidth:'130px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}>{totalValues.totalFineWeight}</td>
                                <td style={{width:'100px',minWidth:'100px',maxWidth:'100px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}>{totalValues.totalWAMT}</td>
                                <td style={{width:'200px',minWidth:'200px',maxWidth:'200px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}>{totalValues.totalSettlementFineWeight}</td>
                                <td style={{width:'260px',minWidth:'260px',maxWidth:'260px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}>{totalValues.totalFinalSettlementNetWeight}</td>
                                <td style={{width:'100px',minWidth:'100px',maxWidth:'100px',height:'52px',padding:'0px 10px',fontWeight:'500',verticalAlign:'middle'}}></td>
                            </tr>
                        </div>
                    </div> 
                   
                <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                    {transactionStatus === TRANSACTION_STATUS.INBOUND_INITIATED ? <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <Button
                            variant='outlined'
                            color='secondary'
                            onClick={() => {
                                if (!inboundDetails.hsnCode) return notify('Add HSN Code first.', 'error')
                                setShowInboundLineForm(true)
                            }} >
                            {'Add New Line'}
                        </Button>
                        <Button variant='outlined' color='secondary' onClick={addMeltingLotHandler}>{'Add Melting Lot'}</Button>
                        {selectedLots?.length > 0 && transactionStatus ==  TRANSACTION_STATUS.INBOUND_INITIATED  && transactionType != TRANSACTION_TYPES.PURITY_TESTING ?   <Button variant='outlined' color='secondary' onClick={handleConvertToPurityTesting}>{'Convert To Purity Testing'}</Button> : <></> }
                    </div> : <></>}
                </div>
            </div>
        )
    }

    return (
        Boolean(showInboundLineForm) ?
            <InboundLine closeInboundLine={closeLineForm} lineId={typeof showInboundLineForm === 'string' ? showInboundLineForm : ''} showSaveButton={!isLineAlreadyIncluded(showInboundLineForm as string)} /> :
            Boolean(showMeltingLotForm) ?
                <MeltingLot lineIds={selectedLines} closeMeltingLot={closeLotForm} lotNumber={typeof showMeltingLotForm === 'string' ? showMeltingLotForm : ''} isLotCompleted={isLotCompleted(showMeltingLotForm as string)} /> :
                <LineLotTables />
    )
}

export default LineDetails